/**
 * Geometry and state colour for the waste line matching rows.
 *
 * Two columns: the e-Manifest lines and the vendor lines. Both lists stay
 * complete, and a vendor line sits level with the e-Manifest line it was
 * matched to, so the pair being compared ends up on one row without either
 * source being hidden behind a menu. A vendor line nothing claimed keeps a row
 * of its own at the point in the vendor's numbering where it falls, which is
 * what makes a dropped line legible: the gap appears next to the lines it is
 * between.
 *
 * A vendor line is dragged to the e-Manifest line it belongs to, so the second
 * column is both a card and a place to drop one. The menu on the e-Manifest
 * line does the same thing from the keyboard and stays the equal path: the
 * drag states here are all appearance.
 *
 * No height cap and no scroller of its own. The shared Dialog scrolls its
 * content already, and a second scrolling box inside it puts two scrollbars
 * side by side with the rows split between them.
 *
 * Written as container selectors against the data attributes the rows carry,
 * rather than an sx prop on every element, so the rows stay readable as markup.
 *
 * Its own module rather than beside the dialog so that file only exports
 * components: mixing a constant in defeats Vite's Fast Refresh and forces a
 * full reload on every edit.
 */
export const pairingListSx = {
  border: 1,
  borderColor: "divider",
  borderRadius: 1,

  "& [data-row]": {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
    columnGap: 2.5,
    px: 1.5,
    py: 1.25,
    alignItems: "stretch",
  },
  // One line between rows rather than a box around each of the two cells on
  // them. The cells are already told apart by the gap between the columns, and
  // a border on every cell puts four lines where the reader needs one.
  "& [data-row] + [data-row]": { borderTop: 1, borderColor: "divider" },

  // The reading preference, above the columns it changes.
  // Detail turns four values into every field the source recorded, so the rows
  // need room to breathe at the same time.
  "&[data-detailed='detailed'] [data-row]": { py: 2 },

  "& [data-toolbar]": {
    display: "flex",
    alignItems: "center",
    px: 1.5,
    pt: 0.5,
    borderBottom: 1,
    borderColor: "divider",
  },

  "& [data-head]": {
    color: "text.disabled",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    fontWeight: 600,
    pt: 1,
    pb: 0.5,
  },

  // The e-Manifest line's label and its menu button, on one line.
  "& [data-line-head]": {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 0.5,
  },

  // A line as one of the two sources holds it. A bar down its left edge
  // carries the state, so the row reads as two columns of values rather than
  // four nested boxes.
  "& [data-cell]": {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 0.25,
    borderLeft: 3,
    borderLeftStyle: "solid",
    borderColor: "transparent",
    pl: 1.25,
  },
  "& [data-cell='agree']": { borderColor: "success.main" },
  "& [data-cell='differ']": { borderColor: "warning.main" },
  "& [data-cell='gap']": { borderLeftStyle: "dashed", borderColor: "divider" },
  // The half of a row where a source has nothing. Dashed and empty rather than
  // absent: a gap is a finding in its own right.
  "& [data-cell='empty']": {
    borderLeftStyle: "dashed",
    borderColor: "divider",
    justifyContent: "center",
  },

  // The menu button that opens the same choice a drag makes. Pulled in by its
  // own margin so a small IconButton does not make the row taller.
  "& [data-pick]": { m: -0.5 },

  // The vendor half of a row, as a place a card can be dropped. A wrapper
  // rather than the card itself, because the slot has to stay a target while
  // it is empty, which is exactly when there is no card to hang the handlers
  // on.
  "& [data-slot]": { display: "flex", minWidth: 0 },
  "& [data-slot] > *": { flex: 1, minWidth: 0 },
  // Marked on the card rather than behind it: the card carries its own fill,
  // so a background on the wrapper is hidden by whatever is sitting in it.
  "& [data-slot='over'] [data-cell]": {
    borderColor: "primary.main",
    borderLeftStyle: "solid",
    bgcolor: "action.hover",
    borderRadius: 1,
  },

  // A vendor line in the hand. Same grab cursor whether it is in a slot or in
  // a row of its own, so the two read as the same object.
  "& [data-drag]": { cursor: "grab" },
  "& [data-drag='dragging']": { opacity: 0.55 },
  "& [data-grip]": { fontSize: "1rem", color: "text.disabled", verticalAlign: "-0.2em" },

  // Where a vendor line goes to pair with nothing. Always present, including
  // when nothing is unpaired, because a drop target that appears only once it
  // is already needed is one nobody can aim at.
  "& [data-tray]": {
    display: "flex",
    alignItems: "center",
    gap: 1,
    flexWrap: "wrap",
    mx: 1.5,
    mt: 0.5,
    mb: 1.5,
    px: 1.25,
    py: 1,
    border: 1,
    borderStyle: "dashed",
    borderColor: "divider",
    borderRadius: 1,
  },
  "& [data-tray='over']": {
    borderStyle: "solid",
    borderColor: "primary.main",
    bgcolor: "action.hover",
  },

  "& [data-line]": { fontWeight: 600, fontVariantNumeric: "tabular-nums" },

  "& [data-chips]": { display: "flex", flexWrap: "wrap", gap: 0.5 },

  // One value the two sources hold. Marked where they disagree about it, so the
  // reader looks at the field rather than at a verdict they then have to place.
  "& [data-chip]": {
    display: "inline-flex",
    alignItems: "baseline",
    gap: 0.5,
    fontVariantNumeric: "tabular-nums",
    fontSize: "0.75rem",
    lineHeight: 1.6,
    px: 0.75,
    borderRadius: 0.5,
    bgcolor: "action.hover",
    color: "text.secondary",
    whiteSpace: "nowrap",
  },
  // Which box the value came from. Quieter than the value, because the reader
  // is comparing numbers and reads the label only when they need to place one.
  "& [data-chip-label]": {
    fontSize: "0.6875rem",
    letterSpacing: "0.04em",
    color: "text.disabled",
    fontWeight: 400,
  },
  "& [data-chip='differs'] [data-chip-label]": { color: "inherit", opacity: 0.75 },
  "& [data-chip='differs']": {
    bgcolor: "warning.100",
    color: "warning.dark",
    fontWeight: 600,
  },

  // Every field the source recorded, for the reader who turned detail on.
  // Values wrap rather than truncate: the shipping description is the longest
  // field on a manifest and the reason to turn it on at all.
  "& [data-fields]": {
    display: "grid",
    gridTemplateColumns: "minmax(0, auto) minmax(0, 1fr)",
    columnGap: 1.25,
    alignItems: "baseline",
  },
  "& [data-field-label]": { color: "text.disabled", whiteSpace: "nowrap", lineHeight: 1.6 },
  "& [data-field-value]": {
    fontSize: "0.8125rem",
    fontVariantNumeric: "tabular-nums",
    overflowWrap: "anywhere",
    lineHeight: 1.6,
  },
  "& [data-field-value='differs']": { color: "warning.dark", fontWeight: 600 },

  "& [data-desc]": {
    fontSize: "0.75rem",
    color: "text.disabled",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
} as const;

/** The colour key under the rows, so the bars do not have to be guessed at. */
export const pairingLegendSx = {
  display: "flex",
  flexWrap: "wrap",
  gap: 2,
  "& [data-key]": { display: "flex", alignItems: "center", gap: 0.75 },
  // The same bar the rows carry, at the size it appears there.
  "& [data-swatch]": {
    width: 0,
    height: 14,
    borderLeft: 3,
    borderLeftStyle: "solid",
    borderColor: "divider",
  },
  "& [data-swatch='agree']": { borderColor: "success.main" },
  "& [data-swatch='differ']": { borderColor: "warning.main" },
  "& [data-swatch='gap']": { borderLeftStyle: "dashed" },
} as const;
